import { Suspense, lazy, type ReactNode } from "react";
import { DRiskifyLogo } from "@/auth/brand/DRiskifyLogo";
import { LoginGlobeBackdrop } from "@/auth/brand/LoginGlobeBackdrop";
import { AuthSpinner } from "./AuthSpinner";
import { GlassAuthCard } from "./GlassAuthCard";

const LoginBrandStory = lazy(() =>
  import("@/auth/brand/LoginBrandStory").then((module) => ({
    default: module.LoginBrandStory,
  })),
);

/**
 * The one composition every screen before the workbench is drawn in: the
 * brand story on the left and the flock globe behind a glass card on the
 * right. The router renders it once around every auth route, so moving from
 * screen to screen swaps only the card. On a phone the story steps aside for a
 * lockup bar and the card is the page; the story stays in the accessibility
 * tree so the page keeps its one h1.
 */
export function AuthStage({ children }: { children: ReactNode }) {
  return (
    <main className="starling-auth-theme min-h-screen overflow-clip bg-[rgb(var(--starling-brand-deep))] px-4 py-5 text-[rgb(var(--starling-text-primary))] sm:px-6 sm:py-6 lg:px-10">
      <div className="mx-auto grid min-h-[calc(100vh-2.5rem)] max-w-[96rem] content-start items-center gap-4 sm:min-h-[calc(100vh-3rem)] lg:grid-cols-[minmax(0,0.92fr)_minmax(38rem,1.08fr)] lg:content-center lg:gap-10">
        <div
          aria-hidden="true"
          data-testid="auth-stage-lockup"
          className="flex items-center justify-center gap-2.5 pt-1 lg:hidden"
        >
          <DRiskifyLogo className="w-8" decorative />
          <span className="text-lg font-semibold">DRiskify</span>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[rgb(var(--starling-text-tertiary))]">
            Starling
          </span>
        </div>
        <div className="max-lg:sr-only">
          <Suspense fallback={null}>
            <LoginBrandStory />
          </Suspense>
        </div>
        <LoginGlobeBackdrop>{children}</LoginGlobeBackdrop>
      </div>
    </main>
  );
}

interface AuthLoadingCardProps {
  title: string;
  body: string;
  eyebrow?: string;
  /** Matches the width of the card that will replace it. */
  size?: "default" | "wide";
}

/** A card waiting on the server, for any screen before the workbench. */
export function AuthLoadingCard({
  title,
  body,
  eyebrow,
  size,
}: AuthLoadingCardProps) {
  return (
    <GlassAuthCard eyebrow={eyebrow} title={title} size={size}>
      <div className="flex items-center gap-3" role="status">
        <span className="text-[rgb(var(--starling-brand-teal))]">
          <AuthSpinner />
        </span>
        <p className="text-sm leading-5 text-[rgb(var(--starling-text-secondary))]">
          {body}
        </p>
      </div>
    </GlassAuthCard>
  );
}

/** A whole page waiting on the server, outside the auth routes. */
export function AuthStageLoading(props: AuthLoadingCardProps) {
  return (
    <AuthStage>
      <AuthLoadingCard {...props} />
    </AuthStage>
  );
}
